/**
 * Vendor Foundation Sprint — Busy Woo Migration UI (2026-08-02)
 *
 * /admin/vendor-migration — Admin drag-drop importer for Busy Woo (or
 * any) vendor JSON / CSV. Talks to:
 *   POST /api/admin/vendor-migration/busy-woo/preview
 *   POST /api/admin/vendor-migration/busy-woo/apply
 *
 * Fully idempotent — the underlying import primitive skips already-
 * imported records without side effects, so it is safe to click
 * Apply multiple times.
 */
import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UploadCloud, FileText, Play, CheckCircle2, XCircle,
  AlertTriangle, Download, Loader2, Info,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SAMPLE_CSV = `email,business_name,owner_name,phone,website,instagram,tier,signup_date,stripe_customer_id,stripe_subscription_id,approval_status,notes
vendor1@example.com,Vendor One LLC,First Last,555-1000,https://vendor1.com,@vendor1,standard,2024-06-01T10:00:00Z,cus_ex_1,,pending_review,Legacy import
vendor2@example.com,Vendor Two Studio,Jane Doe,555-2000,https://vendor2.com,,plus,2024-07-15T14:30:00Z,cus_ex_2,sub_ex_2,approved,VIP`;

const SAMPLE_JSON = `[
  {
    "email": "vendor1@example.com",
    "business_name": "Vendor One LLC",
    "owner_name": "First Last",
    "phone": "555-1000",
    "website": "https://vendor1.com",
    "tier": "standard",
    "signup_date": "2024-06-01T10:00:00Z",
    "stripe_customer_id": "cus_ex_1"
  }
]`;


function csvToRecords(csvText) {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(',');
    const record = {};
    headers.forEach((h, idx) => {
      const v = (cells[idx] || '').trim();
      if (v !== '') record[h] = v;
    });
    if (record.email && record.business_name) rows.push(record);
  }
  return rows;
}


export default function AdminVendorMigration() {
  const [records, setRecords] = useState([]);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = useCallback(async (file) => {
    if (!file) return;
    setFileName(file.name);
    try {
      const text = await file.text();
      let parsed;
      if (file.name.toLowerCase().endsWith('.json')) {
        parsed = JSON.parse(text);
        if (!Array.isArray(parsed)) throw new Error('JSON must be a top-level array');
      } else {
        parsed = csvToRecords(text);
      }
      if (parsed.length === 0) throw new Error('No records found');
      if (parsed.length > 500) throw new Error('Max 500 records per batch — split the file');
      setRecords(parsed);
      setPreview(null);
      setApplyResult(null);
      toast.success(`Loaded ${parsed.length} records from ${file.name}`);
    } catch (e) {
      toast.error(`Failed to parse: ${e.message}`);
      setRecords([]);
    }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  }, [onFile]);

  const runPreview = useCallback(async () => {
    if (records.length === 0) return;
    setBusy(true); setApplyResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/vendor-migration/busy-woo/preview`,
        { records }, AUTH(),
      );
      setPreview(r.data);
      toast.success(`Preview ready: ${r.data.would_create} create · ${r.data.would_link} link · ${r.data.would_skip} skip`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  }, [records]);

  const runApply = useCallback(async () => {
    if (records.length === 0) return;
    if (!window.confirm(
      `APPLY ${records.length} vendor record(s)?\n\n` +
      `Preview said: create=${preview?.would_create} · link=${preview?.would_link} · skip=${preview?.would_skip}\n\n` +
      `This is idempotent — records already imported will be skipped.`
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/vendor-migration/busy-woo/apply`,
        { records, confirm: true }, AUTH(),
      );
      setApplyResult(r.data);
      const { created, linked, already_imported, failed } = r.data;
      toast.success(`Applied: ${created} created · ${linked} linked · ${already_imported} skipped · ${failed} failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally { setBusy(false); }
  }, [records, preview]);

  const download = (name, content, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const summary = useMemo(() => applyResult || preview, [applyResult, preview]);

  return (
    <div className="min-h-[100dvh] bg-slate-50" data-testid="admin-vendor-migration-page">
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <h1 className="text-xl font-bold text-slate-900">Vendor Migration</h1>
          <p className="text-sm text-slate-500 mt-1">
            Import Busy Woo (or any) vendor list. Fully idempotent — safe to re-run any number of times.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Drop zone */}
        <Card
          className="p-8 border-2 border-dashed border-slate-300 hover:border-slate-400 transition-colors cursor-pointer bg-white"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => document.getElementById('vm-file').click()}
          data-testid="admin-vendor-migration-dropzone"
        >
          <input
            id="vm-file"
            type="file"
            accept=".json,.csv"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <div className="text-center">
            <UploadCloud className="w-10 h-10 mx-auto text-slate-400 mb-3" />
            <div className="text-sm text-slate-700 font-medium">
              Drop a <code className="text-xs">.json</code> or <code className="text-xs">.csv</code> file here, or click to browse
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Required columns: <code>email</code>, <code>business_name</code>. Max 500 records per batch.
            </div>
            {fileName && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-xs text-slate-700">
                <FileText className="w-3.5 h-3.5" /> {fileName} · {records.length} records
              </div>
            )}
          </div>
        </Card>

        {/* Template downloads */}
        <div className="flex flex-wrap gap-2 text-xs">
          <Button variant="outline" size="sm" onClick={() => download('busy_woo_sample.csv', SAMPLE_CSV, 'text/csv')} data-testid="admin-vendor-migration-download-csv">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Sample CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => download('busy_woo_sample.json', SAMPLE_JSON, 'application/json')} data-testid="admin-vendor-migration-download-json">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Sample JSON
          </Button>
          <div className="text-slate-500 self-center ml-2">
            <Info className="w-3.5 h-3.5 inline mr-1" />
            <a
              href="/app/memory/WEBSITE_HANDOFF_PACKAGE_2026-08-02.md"
              target="_blank" rel="noreferrer"
              className="hover:underline"
            >
              See §9 of the Website Handoff Package for the full record schema
            </a>
          </div>
        </div>

        {/* Action buttons */}
        {records.length > 0 && (
          <div className="flex gap-2">
            <Button
              onClick={runPreview}
              disabled={busy}
              variant="outline"
              data-testid="admin-vendor-migration-preview-btn"
            >
              {busy && !applyResult ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Preview {records.length} record{records.length === 1 ? '' : 's'}
            </Button>
            <Button
              onClick={runApply}
              disabled={busy || !preview}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="admin-vendor-migration-apply-btn"
            >
              {busy && preview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Apply
            </Button>
          </div>
        )}

        {/* Summary tiles */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatTile label={applyResult ? 'Created' : 'Would Create'} value={summary.would_create ?? summary.created} tone="emerald" testid="admin-vendor-migration-stat-create" />
            <StatTile label={applyResult ? 'Linked' : 'Would Link'} value={summary.would_link ?? summary.linked} tone="indigo" testid="admin-vendor-migration-stat-link" />
            <StatTile label="Would Merge" value={preview?.would_merge ?? 0} tone="fuchsia" testid="admin-vendor-migration-stat-merge" />
            <StatTile label={applyResult ? 'Skipped (already imported)' : 'Already imported'} value={summary.would_skip ?? summary.already_imported} tone="slate" testid="admin-vendor-migration-stat-skip" />
            <StatTile label="Failed" value={summary.failed ?? 0} tone="rose" testid="admin-vendor-migration-stat-fail" />
          </div>
        )}

        {/* Warnings + quality flags banner */}
        {preview && !applyResult && (preview.with_warnings > 0 || preview.with_quality_flags > 0) && (
          <Card className="p-3 bg-amber-50 border-amber-200 text-xs text-amber-900" data-testid="admin-vendor-migration-warnings-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Review before Apply</div>
                <div className="mt-0.5">
                  {preview.with_warnings} record{preview.with_warnings === 1 ? '' : 's'} with duplicate-detection warnings ·{' '}
                  {preview.with_quality_flags} record{preview.with_quality_flags === 1 ? '' : 's'} with data quality flags.
                  Expand any row below to inspect signals & existing capabilities.
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Preview results table */}
        {preview && !applyResult && (
          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50 text-sm font-semibold text-slate-900">
              Preview — {preview.total} records · click any row to inspect validation signals
            </div>
            <div className="max-h-[520px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2 w-6"></th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Business</th>
                    <th className="px-3 py-2">Action</th>
                    <th className="px-3 py-2">Signals</th>
                    <th className="px-3 py-2">Existing ref</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.records.map((r, i) => (
                    <PreviewRow key={i} row={r} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Apply results table */}
        {applyResult && (
          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50 text-sm font-semibold text-slate-900">
              Apply results — {applyResult.total} records
            </div>
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Ref</th>
                    <th className="px-3 py-2">Action</th>
                    <th className="px-3 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {applyResult.results.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-3 py-1.5 font-mono text-[11px]">{r.email}</td>
                      <td className="px-3 py-1.5 font-mono text-[11px]">{r.application_ref || '—'}</td>
                      <td className="px-3 py-1.5">
                        {r.ok ? <ActionBadge action={r.action} /> : (
                          <span className="inline-flex items-center gap-1 text-rose-700">
                            <XCircle className="w-3 h-3" /> failed
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-slate-500">
                        {r.ok ? (r.notes || []).join(' · ') : r.error}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t bg-emerald-50 text-xs text-emerald-800 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> {applyResult.notes}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}


function StatTile({ label, value, tone, testid }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-800 ring-emerald-100',
    indigo:  'bg-indigo-50 text-indigo-800 ring-indigo-100',
    slate:   'bg-slate-100 text-slate-800 ring-slate-200',
    rose:    'bg-rose-50 text-rose-800 ring-rose-100',
  };
  return (
    <div className={`rounded-lg ring-1 p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-2xl font-bold">{value ?? 0}</div>
      <div className="text-xs mt-0.5">{label}</div>
    </div>
  );
}


function ActionBadge({ action }) {
  const map = {
    create:                { label: 'Create',            cls: 'bg-emerald-100 text-emerald-800' },
    link:                  { label: 'Link to existing',  cls: 'bg-indigo-100 text-indigo-800' },
    merge:                 { label: 'Merge required',    cls: 'bg-fuchsia-100 text-fuchsia-800' },
    already_imported:      { label: 'Skip (imported)',   cls: 'bg-slate-100 text-slate-700' },
    // legacy apply-side result codes
    created:               { label: 'Created',           cls: 'bg-emerald-100 text-emerald-800' },
    linked_to_existing:    { label: 'Linked',            cls: 'bg-indigo-100 text-indigo-800' },
  };
  const cfg = map[action] || { label: action, cls: 'bg-slate-100 text-slate-700' };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium ${cfg.cls}`}>
      {(action === 'already_imported' || action === 'merge') && <AlertTriangle className="w-2.5 h-2.5" />}
      {cfg.label}
    </span>
  );
}


function PreviewRow({ row }) {
  const [open, setOpen] = React.useState(false);
  const primary = row.candidates?.find(c => c.same_owner) || row.candidates?.[0];
  const hasIssues = row.warnings?.length || row.quality_flags?.length;
  return (
    <>
      <tr
        className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${open ? 'bg-slate-50' : ''}`}
        onClick={() => setOpen(!open)}
        data-testid={`admin-vendor-migration-row-${row.email}`}
      >
        <td className="px-3 py-1.5 text-slate-400">
          {open ? '▾' : '▸'}
        </td>
        <td className="px-3 py-1.5 font-mono text-[11px]">
          {row.email}
          {row.existing_user && (
            <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-sky-50 text-sky-700 ring-1 ring-sky-100">user exists</span>
          )}
        </td>
        <td className="px-3 py-1.5 truncate max-w-[220px]">{row.business_name}</td>
        <td className="px-3 py-1.5"><ActionBadge action={row.predicted_action} /></td>
        <td className="px-3 py-1.5">
          <div className="flex flex-wrap gap-1">
            {(primary?.signals || []).slice(0, 3).map(s => (
              <span key={s} className="text-[9px] px-1 py-0.5 rounded bg-amber-50 text-amber-800 ring-1 ring-amber-100">{s}</span>
            ))}
            {primary?.has_stripe_customer && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100">stripe</span>
            )}
            {hasIssues > 0 && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-rose-50 text-rose-800 ring-1 ring-rose-100">
                {row.warnings.length + row.quality_flags.length} flag{(row.warnings.length + row.quality_flags.length) === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </td>
        <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500">
          {primary?.application_ref || '—'}
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50/60">
          <td colSpan={6} className="px-3 py-3">
            <div className="space-y-2 text-[11px]">
              {row.warnings?.length > 0 && (
                <div className="p-2 bg-amber-50 rounded ring-1 ring-amber-200 text-amber-900">
                  <div className="font-semibold mb-1">⚠️ Warnings</div>
                  {row.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                </div>
              )}
              {row.quality_flags?.length > 0 && (
                <div className="p-2 bg-slate-100 rounded ring-1 ring-slate-200 text-slate-700">
                  <div className="font-semibold mb-1">Data quality</div>
                  <div className="flex flex-wrap gap-1">
                    {row.quality_flags.map(f => (
                      <span key={f} className="px-1.5 py-0.5 rounded bg-white text-slate-700 ring-1 ring-slate-200">{f}</span>
                    ))}
                  </div>
                </div>
              )}
              {row.existing_user && (
                <div className="p-2 bg-sky-50 rounded ring-1 ring-sky-200 text-sky-900">
                  <div className="font-semibold mb-1">Existing user account</div>
                  role={row.existing_user_role} · active={String(row.existing_user_is_active)} · id={row.existing_user_id}
                </div>
              )}
              {row.candidates?.length > 0 && (
                <div className="p-2 bg-white rounded ring-1 ring-slate-200">
                  <div className="font-semibold text-slate-800 mb-1.5">Matching companies ({row.candidates.length})</div>
                  <div className="space-y-2">
                    {row.candidates.map(c => (
                      <div key={c.company_id} className="p-2 border-l-2 border-slate-300 bg-slate-50/50">
                        <div className="font-medium text-slate-900">
                          {c.company_name}
                          {c.same_owner && (
                            <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-800">same owner</span>
                          )}
                          {c.vendor_profile_id && (
                            <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-slate-200 text-slate-800">has vendor profile</span>
                          )}
                        </div>
                        <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0.5 text-slate-600">
                          <div>company_id: <span className="font-mono">{c.company_id.slice(0, 8)}…</span></div>
                          {c.application_ref && <div>ref: <span className="font-mono">{c.application_ref}</span></div>}
                          {c.approval_status && <div>status: <span className="font-medium">{c.approval_status}</span></div>}
                          {c.vendor_level && <div>tier: <span className="font-medium">{c.vendor_level}</span></div>}
                          {c.has_stripe_customer && <div className="text-emerald-700">✓ Stripe {c.stripe_customer_id}</div>}
                          {c.stripe_subscription_status && <div>sub: {c.stripe_subscription_status}</div>}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {c.signals.map(s => (
                            <span key={s} className="text-[9px] px-1 py-0.5 rounded bg-amber-50 text-amber-800 ring-1 ring-amber-100">match: {s}</span>
                          ))}
                          {c.capabilities.map(cap => (
                            <span key={cap} className={`text-[9px] px-1 py-0.5 rounded ring-1 ${
                              cap === 'book_talent' ? 'bg-fuchsia-50 text-fuchsia-800 ring-fuchsia-100' :
                              cap === 'vendor_marketplace' ? 'bg-indigo-50 text-indigo-800 ring-indigo-100' :
                              cap === 'client_capability' ? 'bg-emerald-50 text-emerald-800 ring-emerald-100' :
                              'bg-slate-100 text-slate-700 ring-slate-200'
                            }`}>{cap.replace('_capability','').replace('_',' ')}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {row.candidates?.length === 0 && (
                <div className="p-2 text-slate-500 italic">No existing company matches — safe to Create.</div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
